/**
 * Shared storefront state that is not the basket.
 *
 * The shop's terms -- name, currency, delivery charge, whether cards can be taken -- are
 * one query used by five screens, so they are cached here under one key rather than fetched
 * per page. `staleTime` is long on purpose: these change about once a year, and refetching
 * them on every navigation would be a request per click for an answer that has not moved.
 *
 * The storefront cannot use `useRegime` for its currency the way the ERP screens do: that
 * endpoint needs a session, and most of the shop is browsed without one. `/marketplace/
 * settings` is the anonymous slice of the same settings row, and `money()` below binds its
 * currency so no call site has to remember to pass it.
 */

import { useQuery } from '@tanstack/react-query'
import { useCallback } from 'react'

import { getStorefrontSettings, type StorefrontSettings } from '@/features/marketplace/api'
import { formatMoney } from '@/lib/money'

export function useStorefrontSettings() {
  const query = useQuery({
    queryKey: ['storefront-settings'],
    queryFn: getStorefrontSettings,
    retry: false,
    staleTime: 10 * 60_000,
  })

  const currency = query.data?.currency
  /** `formatMoney` with the shop's currency already applied. */
  const money = useCallback(
    (amount: string | number | null | undefined) => formatMoney(amount, currency),
    [currency],
  )

  return { settings: query.data as StorefrontSettings | undefined, money, ...query }
}

/**
 * What delivery will cost on a basket of this value, in the shop's own terms.
 *
 * A quote, not a charge: the binding figure is `shipping_amount` on the order the server
 * creates, computed by `marketplace.service.shipping_for` from the same two settings. This
 * exists so the basket can say "delivery ₹49" before the order exists, and so it can say
 * "spend ₹120 more for free delivery", which is the only reason the threshold is public.
 */
export function quoteShipping(
  settings: StorefrontSettings | undefined,
  goodsTotal: number,
): { amount: number; freeOver: number | null; shortfall: number | null } {
  if (!settings) return { amount: 0, freeOver: null, shortfall: null }
  const flat = Number(settings.shipping_flat_rate) || 0
  const freeOver = settings.shipping_free_over == null ? null : Number(settings.shipping_free_over)
  if (flat <= 0) return { amount: 0, freeOver, shortfall: null }
  if (freeOver != null && goodsTotal >= freeOver) {
    return { amount: 0, freeOver, shortfall: null }
  }
  return {
    amount: flat,
    freeOver,
    shortfall: freeOver == null ? null : freeOver - goodsTotal,
  }
}
